import React from 'react';
import {
  Box,
  Button,
  Stack,
  Grid,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/errorMessage';
import SuccessMessage from '../../components/successMessage';
import IncomesModal from './incomesModal';
import ReceiptModal from './receiptModal';
import FinancialResult from '../FinancialResult';
import { useIncomesScreen, incomeStatusConfig } from '../hooks/useIncomesScreen';

export default function IncomesWeb() {
  const {
    incomes,
    totalAmount,
    totalAmountToBeReceived,
    totalAmountReceived,
    isLoading,
    selectedDate,
    setSelectedDate,
    goToPreviousMonth,
    goToNextMonth,
    menuAnchorEl,
    menuSelectedIncome,
    openMenu,
    closeMenu,
    openIncomeModal,
    setOpenIncomeModal,
    openReceiptModal,
    setOpenReceiptModal,
    selectedIncomeId,
    addIncome,
    editIncome,
    receive,
    openDeleteModal,
    clickDelete,
    closeDeleteModal,
    confirmDelete,
    saveSuccess,
    saveError,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  } = useIncomesScreen();

  return (
    <Box sx={{ p: 3 }}>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ justifySelf: { xs: 'center', md: 'start' } }}>
          <FinancialResult selectedDate={selectedDate} />
        </Box>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ justifySelf: 'center' }}>
          <IconButton onClick={goToPreviousMonth}>
            <ChevronLeft />
          </IconButton>
          <DatePicker
            label={'mês e ano'}
            views={['month', 'year']}
            value={selectedDate}
            onChange={(date) => setSelectedDate(date)}
          />
          <IconButton onClick={goToNextMonth}>
            <ChevronRight />
          </IconButton>
        </Stack>

        <Box sx={{ justifySelf: { xs: 'center', md: 'end' } }}>
          <Button data-cy="btn-add-income" variant="outlined" onClick={addIncome}>
            Adicionar Entrada
          </Button>
        </Box>
      </Box>

      <Grid
        container
        sx={{ mt: 2, p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}
      >
        <Grid item sx={{ width: '15%' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Data a Receber
          </Typography>
        </Grid>
        <Grid item sx={{ width: '40%' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Descrição
          </Typography>
        </Grid>
        <Grid item sx={{ width: '10%' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Tipo de Recebimento
          </Typography>
        </Grid>
        <Grid item sx={{ width: '15%', textAlign: 'right' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Valor
          </Typography>
        </Grid>
        <Grid item sx={{ width: '15%', textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Status
          </Typography>
        </Grid>
        <Grid item sx={{ width: '5%' }}></Grid>
      </Grid>

      {incomes.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4, p: 2 }}>
          <Typography>Nenhuma entrada encontrada para o período.</Typography>
        </Box>
      )}

      {incomes.map((income) => (
        <Grid
          container
          key={income.id}
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            alignItems: 'center',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Grid item sx={{ width: '15%' }}>
            <Typography variant="body2">{income.dueDate}</Typography>
          </Grid>

          <Grid item sx={{ width: '40%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{income.description}</Typography>
            </Box>
            {income.observation && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}
              >
                {income.observation}
              </Typography>
            )}
          </Grid>

          <Grid item sx={{ width: '10%' }}>
            <Typography variant="body2">{income.receiptMethodType}</Typography>
          </Grid>

          <Grid item sx={{ width: '15%', textAlign: 'right' }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {income.amount}
            </Typography>
          </Grid>

          <Grid item sx={{ width: '15%', textAlign: 'center' }}>
            <Chip
              label={income.status}
              color={incomeStatusConfig[income.status].color}
              variant={incomeStatusConfig[income.status].variant}
              size="small"
              sx={{ fontWeight: 'bold', minWidth: '85px' }}
            />
          </Grid>

          <Grid item sx={{ width: '5%', textAlign: 'center' }}>
            <IconButton size="small" onClick={(event) => openMenu(event, income)}>
              <MoreVertIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        <MenuItem onClick={editIncome}>Editar</MenuItem>
        <MenuItem onClick={receive} disabled={menuSelectedIncome?.status === 'Recebida'}>
          Receber
        </MenuItem>

        <MenuItem onClick={clickDelete} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      {incomes.length > 0 && (
        <Grid container sx={{ p: 1, mt: 1, justifyContent: 'flex-end' }}>
          <Grid item>
            <Stack direction="column" alignItems="flex-end">
              <Typography variant="caption" color="text.secondary">
                Total: <strong>{totalAmount}</strong>
              </Typography>

              <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
                Total Recebido: <strong>{totalAmountReceived}</strong>
              </Typography>

              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                Total a Receber: <strong>{totalAmountToBeReceived}</strong>
              </Typography>
            </Stack>
          </Grid>

          <Grid item sx={{ width: '5%' }}></Grid>
        </Grid>
      )}

      {openIncomeModal && (
        <IncomesModal
          open={openIncomeModal}
          onClose={() => setOpenIncomeModal(false)}
          onSaveSuccess={saveSuccess}
          incomeId={selectedIncomeId}
          incomeOrigin={'FINANCIAL'}
          patientId={null}
        />
      )}

      {openReceiptModal && (
        <ReceiptModal
          open={openReceiptModal}
          onClose={() => setOpenReceiptModal(false)}
          onSaveSuccess={saveSuccess}
          onSaveError={saveError}
          incomeId={selectedIncomeId}
        />
      )}

      <Dialog open={openDeleteModal} onClose={closeDeleteModal}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir essa entrada?
            <br />
            Essa ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal} color="primary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="error" variant="outlined" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
